import React, { useEffect, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Home, DollarSign, QrCode, Plus, ExternalLink, Copy, Check, Share2 } from "lucide-react";
import HostAwards from "../components/HostAwards";
import EmptyState from "../components/EmptyState";
import { toast } from "sonner";

export default function HostDashboard(){
  const [host, setHost] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [form, setForm] = useState({ name:"", description:"", address:"", city:"Miami", state:"FL", zip:"", guest_capacity:4, amenities:[], images:[""] });

  const load = useCallback(() => api.get("/hosts/me").then(r=>setHost(r.data)).catch(()=>setHost({})), []);
  useEffect(()=>{ load(); },[load]);
  const create = async () => {
    await api.post("/properties", { ...form, guest_capacity:+form.guest_capacity, images:[form.images[0]||"https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=1200"], amenities:["WiFi","Concierge"] });
    setShowCreate(false); load();
  };
  const copyLink = async (id, link) => {
    try {
      const url = link?.startsWith("http") ? link : `${window.location.origin}${link || ""}`;
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      toast.success("Concierge link copied");
      setTimeout(() => setCopiedId(null), 2500);
    } catch {
      toast.error("Copy failed — long-press the link to copy");
    }
  };
  if (!host) return <div className="p-20 text-center">Loading account…</div>;
  if (!host.user_id) {
    return (
      <div className="max-w-md mx-auto p-20 text-center">
        <h2 className="font-serif text-2xl">Account not ready</h2>
        <p className="text-sm text-[#071A2D]/60 mt-2">We couldn&apos;t load your host profile. Please refresh, or contact support if this continues.</p>
        <button onClick={load} className="btn-outline-emerald mt-6">Retry</button>
      </div>
    );
  }
  const leadProp = host.properties?.[0];
  const leadLink = leadProp?.guest_link
    ? (leadProp.guest_link.startsWith("http")
        ? leadProp.guest_link
        : `${window.location.origin}${leadProp.guest_link}`)
    : null;
  return (
    <div className="max-w-7xl mx-auto px-6 py-10 pb-28 lg:pb-10">
      <h1 className="font-serif text-4xl">{host.business_name}</h1>
      <p className="text-[#071A2D]/70 mt-1">Concierge for every property.</p>

      {/* 1. SHARE QR / REFERRAL LINK — lead action for the host */}
      {leadProp && (
        <div
          data-testid="host-share-hero"
          className="mt-7 rounded-3xl overflow-hidden bg-[#071A2D] text-[#FFF8EE] shadow-luxury-lg border border-[#C9A45C]/30 p-6"
        >
          <div className="grid md:grid-cols-[1fr_auto] gap-5 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold flex items-center gap-2">
                <Share2 size={12}/> Your concierge link
              </div>
              <div className="font-serif text-2xl mt-2">{leadProp.name}</div>
              <div className="text-xs opacity-75 mt-1.5 break-all">{leadLink}</div>
              <div className="text-[11px] opacity-65 mt-3">
                Share this link or QR with guests — you earn <strong className="text-[#C9A45C]">5%</strong> on every booking they make through TREL.
              </div>
            </div>
            <div className="flex md:flex-col gap-2 shrink-0">
              <button
                onClick={() => copyLink(leadProp.property_id, leadLink)}
                data-testid="host-share-copy"
                className="text-xs rounded-full bg-[#C9A45C] text-[#071A2D] px-4 py-2.5 font-semibold inline-flex items-center gap-1.5 hover:brightness-110"
              >
                {copiedId === leadProp.property_id ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy link</>}
              </button>
              <Link
                to={leadProp.guest_link}
                target="_blank"
                data-testid="host-share-preview"
                className="text-xs rounded-full bg-[#FFF8EE] text-[#071A2D] px-4 py-2.5 font-semibold inline-flex items-center gap-1.5 hover:brightness-95"
              >
                <ExternalLink size={12}/> Preview page
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* 2. EARNINGS / ACTIVITY SUMMARY */}
      <div className="grid md:grid-cols-3 gap-4 mt-8">
        <Stat icon={Home} label="Properties" value={host.properties?.length || 0}/>
        <Stat icon={DollarSign} label="Earnings" value={`$${host.earnings}`}/>
        <Stat icon={QrCode} label="Concierge views" value="—"/>
      </div>

      {/* 3. PROPERTIES — each tile gets its own copy-link button */}
      <div className="mt-10 flex items-end justify-between">
        <h2 className="font-serif text-2xl">Your properties</h2>
        <button onClick={()=>setShowCreate(true)} className="btn-emerald flex items-center gap-2"><Plus size={16}/> Add property</button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
        {host.properties?.map(p=>(
          <div key={p.property_id} className="rounded-3xl overflow-hidden card-luxury">
            <img src={p.images?.[0]} className="w-full h-40 object-cover" alt=""/>
            <div className="p-5">
              <div className="font-serif text-xl">{p.name}</div>
              <div className="text-xs text-[#071A2D]/60 mt-1">{p.city}, {p.state}</div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => copyLink(p.property_id, p.guest_link)}
                  data-testid={`host-property-copy-${p.property_id}`}
                  className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#064E3B] text-[#FFF8EE] hover:brightness-110"
                >
                  {copiedId === p.property_id ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy link</>}
                </button>
                <Link to={p.guest_link} target="_blank" className="text-xs text-[#FF7A45] inline-flex items-center gap-1">
                  Open <ExternalLink size={11}/>
                </Link>
              </div>
            </div>
          </div>
        ))}
        {!host.properties?.length && (
          <div className="col-span-3">
            <EmptyState
              testid="host-empty-state"
              icon={Home}
              title="Share your link to start earning."
              subtitle="Add your first property to generate a custom Concierge page guests can access from a QR code or short link — you earn 5% on every booking."
              cta={{
                onClick: () => setShowCreate(true),
                label: "Add my first property →",
                testid: "host-empty-cta",
              }}
              tone="luxury"
            />
          </div>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-[#071A2D]/40 flex items-center justify-center z-50 p-4">
          <div className="bg-[#FFF8EE] rounded-3xl p-8 max-w-lg w-full">
            <h3 className="font-serif text-2xl">Add property</h3>
            <div className="mt-5 space-y-4">
              <input placeholder="Property name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="kbd-input"/>
              <textarea placeholder="Description" rows="2" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} className="kbd-input"/>
              <div className="grid grid-cols-2 gap-4">
                <input placeholder="Address" value={form.address} onChange={e=>setForm({...form,address:e.target.value})} className="kbd-input"/>
                <input placeholder="ZIP" value={form.zip} onChange={e=>setForm({...form,zip:e.target.value})} className="kbd-input"/>
                <input placeholder="City" value={form.city} onChange={e=>setForm({...form,city:e.target.value})} className="kbd-input"/>
                <input placeholder="State" value={form.state} onChange={e=>setForm({...form,state:e.target.value})} className="kbd-input"/>
                <input placeholder="Image URL" value={form.images[0]} onChange={e=>setForm({...form,images:[e.target.value]})} className="kbd-input col-span-2"/>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={create} className="btn-coral flex-1">Create</button>
              <button onClick={()=>setShowCreate(false)} className="btn-outline-emerald">Cancel</button>
            </div>
          </div>
        </div>
      )}
      <HostAwards/>
    </div>
  );
}
const Stat = ({icon:I,label,value}) => (
  <div className="p-5 rounded-2xl card-luxury">
    <I className="text-[#064E3B]" size={18}/>
    <div className="text-xs uppercase tracking-widest text-[#071A2D]/60 mt-3">{label}</div>
    <div className="text-2xl font-serif mt-1">{value}</div>
  </div>
);
